'use client';

import { useEffect, useRef, useState } from 'react';
import {
  createPdfPreviewUrl,
  extractPdfPageTexts,
  formatFileSize,
  getRenderScale,
  isPdfFile,
  openPdfDocument,
  renderPdfPageToCanvas,
  type PdfJsDocument,
  type PdfJsPage,
} from '@/lib/browser-pdf-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';

type CompareSource = {
  id: string;
  file: File;
  name: string;
  size: number;
  pageCount: number;
  previewUrl: string | null;
};

type CompareResult = {
  pageNumber: number;
  status: 'same' | 'changed' | 'added' | 'removed';
  reason: string;
};

async function readSource(file: File): Promise<CompareSource> {
  const document = await openPdfDocument(new Uint8Array(await file.arrayBuffer()));

  try {
    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      pageCount: document.numPages,
      previewUrl: await createPdfPreviewUrl(document),
    };
  } finally {
    document.cleanup?.();
    await document.destroy?.();
  }
}

async function renderedPagesDiffer(leftPage: PdfJsPage, rightPage: PdfJsPage) {
  const leftViewport = leftPage.getViewport({ scale: 1 });
  const rightViewport = rightPage.getViewport({ scale: 1 });
  const width = Math.max(leftViewport.width, rightViewport.width);
  const height = Math.max(leftViewport.height, rightViewport.height);

  if (
    Math.abs(leftViewport.width - rightViewport.width) > 2 ||
    Math.abs(leftViewport.height - rightViewport.height) > 2
  ) {
    return true;
  }

  const renderScale = Math.min(
    getRenderScale(leftViewport, 0.7),
    getRenderScale(rightViewport, 0.7),
  );
  const left = await renderPdfPageToCanvas(leftPage, renderScale);
  const right = await renderPdfPageToCanvas(rightPage, renderScale);
  const leftContext = left.canvas.getContext('2d');
  const rightContext = right.canvas.getContext('2d');

  if (!leftContext || !rightContext) {
    return true;
  }

  const leftData = leftContext.getImageData(0, 0, left.canvas.width, left.canvas.height).data;
  const rightData = rightContext.getImageData(0, 0, right.canvas.width, right.canvas.height).data;

  left.canvas.width = 0;
  left.canvas.height = 0;
  right.canvas.width = 0;
  right.canvas.height = 0;

  // Both pages come out of the same renderer at the same scale, so two identical
  // pages differ by exactly zero and there is no antialiasing noise to absorb. That
  // means every pixel can be counted rather than sampled.
  //
  // Sampling was the bug worth remembering: this used to read one pixel every 28
  // points and call the page changed only after seven hits. Text is mostly
  // whitespace, so three edited lines of 11pt type could land between the sample
  // points and be reported as no change at all, which is precisely the edit someone
  // opens a document comparison to find.
  if (leftData.length !== rightData.length) {
    return true;
  }

  const totalPixels = leftData.length / 4;
  let changedPixels = 0;

  for (let index = 0; index < leftData.length; index += 4) {
    const delta =
      Math.abs(leftData[index] - rightData[index]) +
      Math.abs(leftData[index + 1] - rightData[index + 1]) +
      Math.abs(leftData[index + 2] - rightData[index + 2]);

    if (delta > 36) {
      changedPixels += 1;
    }
  }

  // A floor as well as a ratio: on a large page a single changed word is a tiny
  // fraction of the sheet, and on a small one a stray pixel should not count.
  return changedPixels > Math.max(40, totalPixels * 0.0002);
}

export default function BrowserComparePdf() {
  const leftInputRef = useRef<HTMLInputElement | null>(null);
  const rightInputRef = useRef<HTMLInputElement | null>(null);
  const [leftSource, setLeftSource] = useState<CompareSource | null>(null);
  const [rightSource, setRightSource] = useState<CompareSource | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [results, setResults] = useState<CompareResult[]>([]);

  useEffect(
    () => () => {
      if (leftSource?.previewUrl) {
        URL.revokeObjectURL(leftSource.previewUrl);
      }
      if (rightSource?.previewUrl) {
        URL.revokeObjectURL(rightSource.previewUrl);
      }
    },
    [leftSource, rightSource],
  );

  function replaceSource(side: 'left' | 'right', nextSource: CompareSource | null) {
    if (side === 'left') {
      setLeftSource((current) => {
        if (current?.previewUrl) {
          URL.revokeObjectURL(current.previewUrl);
        }
        return nextSource;
      });
      return;
    }

    setRightSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }
      return nextSource;
    });
  }

  async function handleFileSelection(side: 'left' | 'right', file: File | null) {
    setErrorMessage(null);
    setCreditMessage(null);
    setResults([]);

    if (!file) {
      replaceSource(side, null);
      return;
    }

    if (!isPdfFile(file)) {
      replaceSource(side, null);
      setErrorMessage('Choose PDF files on both sides.');
      return;
    }

    try {
      replaceSource(side, await readSource(file));
    } catch (error) {
      replaceSource(side, null);
      setErrorMessage(error instanceof Error ? error.message : 'This PDF could not be opened.');
    }
  }

  async function runCompare() {
    if (!leftSource || !rightSource) {
      setErrorMessage('Choose two PDF files first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setResults([]);

    let leftDocument: PdfJsDocument | null = null;
    let rightDocument: PdfJsDocument | null = null;

    try {
      const credits = await consumeToolUsage(
        'compare-pdf',
        'Credits are not available for another PDF compare right now.',
      );

      leftDocument = await openPdfDocument(new Uint8Array(await leftSource.file.arrayBuffer()));
      rightDocument = await openPdfDocument(new Uint8Array(await rightSource.file.arrayBuffer()));

      const pageCount = Math.max(leftDocument.numPages, rightDocument.numPages);
      const nextResults: CompareResult[] = [];
      const commonPages = Math.min(leftDocument.numPages, rightDocument.numPages);
      const [leftTexts, rightTexts] = await Promise.all([
        extractPdfPageTexts(
          leftDocument,
          Array.from({ length: commonPages }, (_, index) => index + 1),
        ),
        extractPdfPageTexts(
          rightDocument,
          Array.from({ length: commonPages }, (_, index) => index + 1),
        ),
      ]);

      for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
        if (pageNumber > leftDocument.numPages) {
          nextResults.push({
            pageNumber,
            status: 'added',
            reason: 'Page exists only in the newer document.',
          });
          continue;
        }

        if (pageNumber > rightDocument.numPages) {
          nextResults.push({
            pageNumber,
            status: 'removed',
            reason: 'Page exists only in the older document.',
          });
          continue;
        }

        const leftText = leftTexts[pageNumber - 1]?.text ?? '';
        const rightText = rightTexts[pageNumber - 1]?.text ?? '';

        if (leftText !== rightText) {
          nextResults.push({
            pageNumber,
            status: 'changed',
            reason: 'Selectable text changed on this page.',
          });
          continue;
        }

        const leftPage = await leftDocument.getPage(pageNumber);
        const rightPage = await rightDocument.getPage(pageNumber);
        try {
          const changed = await renderedPagesDiffer(leftPage, rightPage);
          nextResults.push({
            pageNumber,
            status: changed ? 'changed' : 'same',
            reason: changed
              ? 'The rendered page looks different even though the extracted text matches.'
              : 'No text or visual change was detected.',
          });
        } finally {
          leftPage.cleanup?.();
          rightPage.cleanup?.();
        }
      }

      setResults(nextResults);
      setCreditMessage(formatToolCreditSuccess('PDF compare', credits));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'The PDFs could not be compared.');
    } finally {
      setIsProcessing(false);
      leftDocument?.cleanup?.();
      await leftDocument?.destroy?.();
      rightDocument?.cleanup?.();
      await rightDocument?.destroy?.();
    }
  }

  const changedCount = results.filter((result) => result.status !== 'same').length;

  return (
    <article className="project-detail-card tool-live-card">
      <div className="tool-live-heading">
        <div>
          <h2>Compare two PDF files page by page</h2>
          <p>
            Check text changes, added pages, removed pages, and visual differences without exporting
            the documents to a server.
          </p>
        </div>
        <span className="tool-live-badge">Compare</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <div className="tool-live-control-grid">
            <button
              className="tool-live-dropzone"
              onClick={() => leftInputRef.current?.click()}
              type="button"
            >
              <div>
                <strong>{leftSource ? leftSource.name : 'Choose the first PDF'}</strong>
                <span>
                  {leftSource ? formatFileSize(leftSource.size) : 'Original or older version'}
                </span>
              </div>
            </button>
            <button
              className="tool-live-dropzone"
              onClick={() => rightInputRef.current?.click()}
              type="button"
            >
              <div>
                <strong>{rightSource ? rightSource.name : 'Choose the second PDF'}</strong>
                <span>
                  {rightSource ? formatFileSize(rightSource.size) : 'Updated or newer version'}
                </span>
              </div>
            </button>
          </div>
          <input
            ref={leftInputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection('left', event.target.files?.[0] ?? null)}
            type="file"
          />
          <input
            ref={rightInputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection('right', event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!leftSource || !rightSource || isProcessing}
              onClick={() => void runCompare()}
              type="button"
            >
              {isProcessing ? 'Comparing…' : 'Compare PDFs'}
            </button>
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Compare summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>First PDF</strong>
              <span>{leftSource ? `${leftSource.pageCount} pages` : '—'}</span>
            </div>
            <div>
              <strong>Second PDF</strong>
              <span>{rightSource ? `${rightSource.pageCount} pages` : '—'}</span>
            </div>
            <div>
              <strong>Changed pages</strong>
              <span>{results.length ? changedCount : 'Not compared yet'}</span>
            </div>
            <div>
              <strong>Output</strong>
              <span>On-screen diff only</span>
            </div>
          </div>
        </aside>
      </div>

      {results.length ? (
        <div className="tool-live-preview">
          <h3>Page differences</h3>
          <div className="tool-live-results-grid">
            {results.map((result) => (
              <article className="tool-result-card" key={`${result.pageNumber}-${result.status}`}>
                <strong>{`Page ${result.pageNumber}`}</strong>
                <span>{result.status.toUpperCase()}</span>
                <span>{result.reason}</span>
              </article>
            ))}
          </div>
        </div>
      ) : null}

      {leftSource?.previewUrl || rightSource?.previewUrl ? (
        <div className="tool-live-preview">
          <h3>Source previews</h3>
          <div className="tool-live-results-grid">
            {leftSource?.previewUrl ? (
              <article className="tool-result-card">
                <img
                  alt="First PDF preview"
                  className="tool-result-image"
                  src={leftSource.previewUrl}
                />
                <strong>{leftSource.name}</strong>
              </article>
            ) : null}
            {rightSource?.previewUrl ? (
              <article className="tool-result-card">
                <img
                  alt="Second PDF preview"
                  className="tool-result-image"
                  src={rightSource.previewUrl}
                />
                <strong>{rightSource.name}</strong>
              </article>
            ) : null}
          </div>
        </div>
      ) : null}
    </article>
  );
}
